import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "../../../composition/module/ModuleFlowExecutor";
import { ILogger } from "../../../logger/ILogger";
import { MyBuildingsModule } from "./MyBuildingsModule";
import { MyBuildingsPopupScene, MyBuildingsSceneOptions } from "./scenes/MyBuildingsPopupScene";
import { SceneManager } from "../../../../scene/SceneManager";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { VariableSaver } from "../../../../tools/variableSaver/VariableSaver";
import { IGuestModel } from "../../common/models/IGuestModel";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { MyBuildingsTutorialResourcePackage } from "./resources/MyBuildingsTutorialResourcePackage";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { IRenterPolicyModel } from "../../common/models/Renters/IRenterPolicyModel";
import { IConnectionModel } from "../../../networking/IConnectionModel";
import { CancellationTokenSource } from "../../../../shared/async/CancellationTokenSource";
import { nameof } from "../../../../shared/bindable-data";
import { TopPanel } from "../../game/components/toppanel/TopPanel";

type MyBuildingsPopupSceneFactory = (sceneOptions: MyBuildingsSceneOptions) => Promise<MyBuildingsPopupScene>;

@injectable()
export class MyBuildingsModuleFlowExecutor extends ModuleFlowExecutor<MyBuildingsModule, boolean> {
    private static readonly NO_CACHE_TAG = "my_buildings_buildings";

    private readonly sceneManager: SceneManager;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly renterPolicy: IRenterPolicyModel;
    private readonly connectionModel: IConnectionModel;
    private readonly variableSaver: VariableSaver;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly topPanel: TopPanel;
    private readonly logger: ILogger;

    private readonly cancellationTokenSource: CancellationTokenSource;

    private topPanelOnNotificationAfterExecute: boolean;

    public constructor(
        sceneManager: SceneManager,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IRenterPolicyModel") renterPolicy: IRenterPolicyModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        variableSaver: VariableSaver,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.sceneManager = sceneManager;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.renterPolicy = renterPolicy;
        this.connectionModel = connectionModel;
        this.variableSaver = variableSaver;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.cancellationTokenSource = new CancellationTokenSource();
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
        this.topPanel.onLayerChanged.add(this.onTopPanelLayerChanged, this);
    }

    public async execute(module: MyBuildingsModule): Promise<boolean> {
        const cancellationToken = this.cancellationTokenSource.token;
        const sceneOptions: MyBuildingsSceneOptions = {
            isFirstShow: false,
            buildingsResources: null,
            tutorialResources: null,
            cancellationToken
        };
        try {
            this.asyncOperationNotification.show();

            if (this.firstTimeExperienceModel.tooltipsInfo.value?.upgradeIntro.value && this.playerModel.buildings.value[0].upgrades.length > 0) {
                sceneOptions.isFirstShow = true;
                const tutorialResources = new MyBuildingsTutorialResourcePackage();
                await tutorialResources.load();
                sceneOptions.tutorialResources = tutorialResources;
                if (cancellationToken.isCancellationRequested) {
                    return false;
                }
            }

            const resourcesToLoad = this.playerModel.buildings.value.flatMap((building) => {
                return [building?.imageUrl.value, ...building.upgrades.map(upgradeItem => upgradeItem?.imageUrl.value)];
            });

            const buildingsResources = new NoCacheDynamicPackage(MyBuildingsModuleFlowExecutor.NO_CACHE_TAG);
            for (const imageUrl of resourcesToLoad) {
                buildingsResources.add(imageUrl);
            }
            for (const building of this.playerModel.buildings.value) {
                if (building.piggyClubImageUrl.value != null && building.piggyClubImageUrl.value != "") {
                    buildingsResources.add(building.piggyClubImageUrl.value);
                }
            }
            await buildingsResources.load();
            if (cancellationToken.isCancellationRequested) {
                return false;
            }
            sceneOptions.buildingsResources = buildingsResources;

            const factory: MyBuildingsPopupSceneFactory = await module.resolve("MyBuildingsPopupSceneFactory");

            await this.renterPolicy.updateRenterPolicyStatus();
            if (cancellationToken.isCancellationRequested) {
                return false;
            }

            const scene = await factory(sceneOptions);
            if (cancellationToken.isCancellationRequested) {
                return false;
            }

            this.asyncOperationNotification.hide();

            this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
            if (!this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront(true);
            }
            const result = await this.sceneManager.showViewableAsync(scene);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront();
            }
            else {
                this.topPanel.bringToOriginParent();
            }

            if (cancellationToken.isCancellationRequested) {
                return false;
            }

            this.playerModel.buildings.value.forEach((item) => {
                if (!this.variableSaver.exist(`${this.guestModel.id.value},${item.id.value}`)) {
                    this.variableSaver.saveBoolean(`${this.guestModel.id.value},${item.id.value}`, true);
                }
            });

            return result;
        }
        catch (e: unknown) {
            this.logger.error("Exception on executing MyBuildings flow: ", e);
            return false;
        }
        finally {
            this.cancellationTokenSource.dispose();
            this.connectionModel.propertyChanged.remove(this.onConnectionModelPropertyChanged, this);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            this.asyncOperationNotification.hide();
            sceneOptions.buildingsResources?.destroy();
            sceneOptions.tutorialResources?.destroy();
        }
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug("MyBuildingsModule cancellation requested, reason: disconnect");
                    this.cancellationTokenSource.cancel();
                }
                break;
        }
    }

    private onTopPanelLayerChanged(): void {
        this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
        if (!this.topPanel.onNotificationLayer) {
            this.topPanel.bringToFront(true);
        }
    }
}
